import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Images, ArrowRight, Star } from "lucide-react";
import { PROPERTIES, HOST_NAME, MOSAIC_PHOTOS, HERO_POOL, getDailyHero } from "../lib/properties";

const SLIDESHOW_INTERVAL_MS = 2000;

export default function Landing() {
  // Start from today's deterministic hero so first paint doesn't flash a different image.
  const startIdx = useMemo(() => {
    const today = getDailyHero();
    const i = HERO_POOL.findIndex((h) => h.src === today.src);
    return i >= 0 ? i : 0;
  }, []);
  const [heroIdx, setHeroIdx] = useState(startIdx);
  const hero = HERO_POOL[heroIdx];

  // Auto-advance every 2 seconds.
  useEffect(() => {
    const id = setInterval(() => {
      setHeroIdx((i) => (i + 1) % HERO_POOL.length);
    }, SLIDESHOW_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Preload every hero image once so subsequent crossfades are instant.
  useEffect(() => {
    HERO_POOL.forEach((h) => {
      const img = new Image();
      img.src = h.src;
    });
  }, []);

  return (
    <div>
      {/* HERO — clean, single screen focus */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <AnimatePresence initial={false}>
            <motion.div
              key={hero.src}
              initial={{ opacity: 0, scale: 1.06 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                opacity: { duration: 1.1, ease: [0.22, 1, 0.36, 1] },
                scale: { duration: 2.6, ease: "linear" },
              }}
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${hero.src})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              data-testid="hero-slideshow-slide"
            />
          </AnimatePresence>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/60 to-white" />

        {/* Featured today chip */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
          <Link
            to={`/nest/${PROPERTIES.find((p) => p.name === hero.nest)?.id || "nest-1"}`}
            data-testid="hero-featured-chip"
            className="inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur-md border border-white/60 shadow-sm px-3 py-1.5 text-[11px] font-medium text-[#111]"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#111] animate-pulse" />
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={hero.nest}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.35 }}
              >
                Now showing · {hero.nest}
              </motion.span>
            </AnimatePresence>
          </Link>
        </div>

        <div className="relative w-full px-5 pt-16 pb-10 text-center">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-[10px] uppercase tracking-[0.25em] text-[#111] font-semibold"
          >
            Guest gallery · {HOST_NAME}&rsquo;s nests
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="font-heading text-[34px] leading-[1.05] font-extrabold text-[#111] tracking-tighter mt-4"
          >
            Snap it.
            <br />
            <span className="text-[#555]">Note it. Keep it.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mt-4 text-[15px] text-[#333] leading-relaxed"
          >
            A picture from your stay, one note under it, printable polaroid — in
            under a minute.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-7 flex flex-col items-center justify-center gap-2.5"
          >
            <Link
              to="/sign?camera=1"
              data-testid="hero-camera-cta"
              className="gb-btn-primary inline-flex items-center gap-2 text-base px-8 py-3.5 w-full max-w-[280px] justify-center"
            >
              <Camera size={18} />
              Take a picture
            </Link>
            <Link
              to="/gallery"
              data-testid="hero-gallery-cta"
              className="gb-btn-ghost inline-flex items-center gap-2 w-full max-w-[280px] justify-center text-sm"
            >
              <Images size={15} />
              Browse gallery
            </Link>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="mt-4 text-[11px] text-[#8a8a8a]"
          >
            No account needed.{" "}
            <Link
              to="/sign"
              data-testid="hero-upload-link"
              className="underline underline-offset-2 text-[#111]"
            >
              Upload instead
            </Link>
          </motion.p>
        </div>
      </section>

      {/* THE NESTS — condensed strip */}
      <section className="px-4 mt-6 pb-8">
        <div className="text-center">
          <p className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-[#111111] font-semibold">
            The nests
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#111] mt-2 tracking-tighter">
            Five little homes across Jakarta.
          </h2>
          <p className="text-sm sm:text-base text-[#5b5b5b] mt-2 max-w-lg mx-auto">
            Tap a nest to see its listing on Airbnb.
          </p>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 justify-items-center">
          {PROPERTIES.map((p, idx) => (
            <motion.a
              key={p.id}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`property-${p.id}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.05, duration: 0.4 }}
              className={`block polaroid ${idx % 2 === 0 ? "tilt-left" : "tilt-right"} w-full max-w-[180px]`}
            >
              <div className="polaroid-tape" aria-hidden="true" />
              <div className="polaroid-photo">
                <img
                  src={p.image}
                  alt={p.name}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-1.5 right-1.5 bg-white/95 rounded-full px-2 py-0.5 text-[10px] font-medium text-[#111] flex items-center gap-1 shadow-sm">
                  <Star size={9} className="text-[#111]" fill="#222" />
                  {p.rating.toFixed(2)}
                </div>
              </div>
              <div className="polaroid-caption">
                {p.name}
                <span className="sub">{p.location}</span>
              </div>
            </motion.a>
          ))}
        </div>

        <p className="mt-10 text-center text-xs text-[#717171]">
          Hosted by <span className="font-semibold text-[#111]">{HOST_NAME}</span> · Superhost · 2 years hosting
        </p>
      </section>

      {/* MOSAIC — moments across all nests */}
      <section className="px-3 mt-2 pb-6">
        <div className="text-center mb-4">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#111] font-semibold">
            Moments
          </p>
          <h2 className="font-heading text-2xl font-extrabold text-[#111] mt-1 tracking-tighter">
            A peek inside every nest.
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {MOSAIC_PHOTOS.map((src, i) => (
            <motion.div
              key={src}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: (i % 6) * 0.05, duration: 0.5 }}
              className={`overflow-hidden bg-[#f2f2f2] ${
                i === 0
                  ? "col-span-2 row-span-2 aspect-square"
                  : i % 5 === 0
                    ? "aspect-[4/5]"
                    : "aspect-square"
              }`}
              data-testid={`mosaic-photo-${i}`}
            >
              <img
                src={src}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
